import {isEmpty} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {getTags} from '../../../../../libs/inboxes/store/src/inboxesSelectors';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  collectRecordKeys,
  isDefined,
} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {onlyItem} from '../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {TicketingPreferencesModel} from '../../../../../libs/shared/core/src/models/api/ticketingPreferencesModel';
import {TicketStatusModel} from '../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {CustomPill} from '../../../../../libs/shared/ui/src/components/pill/featurePill';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {TableCellProps} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  StyledTextCellContainerDiv,
  StyledTextCellValueDiv,
} from '../../../../../libs/shared/ui/src/components/tables/cells/textCell';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {useEditTicketStatusMutation} from '../../../../queries/ticketingQueries';
import {useTicketingSettingsHelpers} from '../../../../settings/companyPreferences/helpers/ticketingPreferencesHelpers';
import {TicketStatusIndicator} from '../../../inboxes/ticketing/ticketStatusIndicator';

export interface TicketStatusCellProps extends TableCellProps<TicketStatusModel> {}

const StyledTicketStatusIndicatorWrapper = styled.div`
  padding: 8px;
  margin-right: 8px;

  border-radius: 100%;
  border: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledTextCellValueDivWrapper = styled(StyledTextCellValueDiv)`
  font-weight: ${fontWeights.bold};
`;

const StyledActionsDiv = styled.div`
  margin-left: auto;
`;

const StyledDefaultPill = styled(CustomPill)`
  margin-left: 4px;
`;

export const TicketStatusCell: FC<TicketStatusCellProps> = ({
  isSelected,
  isHovering,
  isLoading,
  size,
  settings,
  index,
  shouldShowStickyGradient,
  shouldRemoveBottomBorderRadius,
  shouldRemoveTopBorderRadius,
  shouldRemoveLeftBorderRadius,
  value,
}) => {
  const ticketStatusTagId = value?.tagId;
  const ticketStatusTag = useAppSelector((state) =>
    isDefined(ticketStatusTagId) ? getTags(state)[ticketStatusTagId] : undefined,
  );
  const isArchived = Boolean(ticketStatusTag?.archivedAt);

  const {ticketStatusesQueryResult, navigateToEditTicketStatus} = useTicketingSettingsHelpers();

  const isDefaultTicketStatusForSomeCategory =
    isDefined(ticketStatusTagId) &&
    computeIsDefaultTicketStatusForSomeCategory(
      ticketStatusTagId,
      ticketStatusesQueryResult?.ticketingPreferences.statusCategories,
    );

  return (
    <TableCell<TicketStatusModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      shouldRemoveLeftBorderRadius={shouldRemoveLeftBorderRadius}
      renderCell={(ticketStatus) => (
        <StyledTextCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledTicketStatusIndicatorWrapper>
            {ticketStatus ? (
              <TicketStatusIndicator category={ticketStatus.category} />
            ) : (
              <Icon name="squareFilledSmall" />
            )}
          </StyledTicketStatusIndicatorWrapper>
          <StyledTextCellValueDivWrapper $isSelected={Boolean(isSelected)}>
            {ticketStatusTag?.name}
          </StyledTextCellValueDivWrapper>
          {isDefaultTicketStatusForSomeCategory && (
            <StyledDefaultPill color={PaletteColorsEnum.GREY}>Default</StyledDefaultPill>
          )}
          <StyledActionsDiv>
            <PopoverCoordinator
              renderButton={() =>
                isHovering &&
                !isSelected && (
                  <IconButton
                    label={<Trans id="ai-tagging-actions">Actions</Trans>}
                    iconName="moreVertical"
                    size={VisualSizesEnum.SMALL}
                  />
                )
              }
              renderModal={(popoverProps) => (
                <TicketStatusActionsDropdown
                  popoverProps={popoverProps}
                  ticketStatus={ticketStatus}
                  isLastTicketStatusInCategory={computeIsLastTicketStatusInCategory(
                    ticketStatus,
                    ticketStatusesQueryResult?.ticketingPreferences.statuses,
                  )}
                  isDefaultTicketStatusForSomeCategory={isDefaultTicketStatusForSomeCategory}
                  isArchived={isArchived}
                  onEditTicketStatusClick={() => navigateToEditTicketStatus(ticketStatus.tagId)}
                />
              )}
            />
          </StyledActionsDiv>
        </StyledTextCellContainerDiv>
      )}
    />
  );
};

const TicketStatusActionsDropdown = ({
  popoverProps,
  ticketStatus,
  isLastTicketStatusInCategory,
  isDefaultTicketStatusForSomeCategory,
  isArchived,
  onEditTicketStatusClick,
}: {
  popoverProps: PopoverTransferProps;
  ticketStatus: TicketStatusModel;
  isLastTicketStatusInCategory: boolean;
  isDefaultTicketStatusForSomeCategory: boolean;
  isArchived: boolean;
  onEditTicketStatusClick: () => void;
}) => {
  const editTicketStatusMutation = useEditTicketStatusMutation();

  const canArchive = !isDefaultTicketStatusForSomeCategory && !isLastTicketStatusInCategory;

  const onToggleArchiveTicketStatusClick = async () => {
    const shouldArchive = !isArchived;

    if (shouldArchive && !canArchive) {
      reportToBugsnag(new Error('Cannot archive ticket status'));
      return;
    }

    await editTicketStatusMutation.mutateAsync({
      ticketStatusTagId: ticketStatus.tagId,
      payload: {archive: shouldArchive},
    });
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SimpleDropdown {...popoverProps}>
      {isArchived ? (
        <SimpleDropdownItem onSelect={onToggleArchiveTicketStatusClick}>
          <Trans id="table-cell-ticket-status-actions-restore">Restore status</Trans>
        </SimpleDropdownItem>
      ) : (
        <>
          <SimpleDropdownItem onSelect={onEditTicketStatusClick}>
            <Trans id="table-cell-ticket-status-actions-edit">Edit status</Trans>
          </SimpleDropdownItem>
          {!isLastTicketStatusInCategory && (
            <SimpleDropdownItem
              onSelect={onToggleArchiveTicketStatusClick}
              renderTooltip={() => maybeRenderArchiveDisabledTooltip(isDefaultTicketStatusForSomeCategory)}
              isDisabled={!canArchive}
            >
              <Trans id="table-cell-ticket-status-actions-archive">Archive status</Trans>
            </SimpleDropdownItem>
          )}
        </>
      )}
    </SimpleDropdown>
  );
};

function maybeRenderArchiveDisabledTooltip(isDefaultTicketStatusInCategory: boolean) {
  if (isDefaultTicketStatusInCategory) {
    return (
      <Trans id="table-cell-ticket-status-actions-archive-tooltip-disabled-default-ticket-status">
        Cannot archive the default ticket status of a category
      </Trans>
    );
  }

  return null;
}

/*
 * Helpers.
 */

function computeIsLastTicketStatusInCategory(
  ticketStatus: TicketStatusModel,
  ticketStatuses: ReadonlyArray<TicketStatusModel> | undefined = [],
): boolean {
  return isEmpty(
    ticketStatuses.filter(
      (status) => status.category === ticketStatus.category && status.tagId !== ticketStatus.tagId,
    ),
  );
}

function computeIsDefaultTicketStatusForSomeCategory(
  ticketStatusTagId: number,
  ticketStatusCategories: TicketingPreferencesModel['statusCategories'] | undefined,
) {
  return isDefined(
    ticketStatusCategories &&
      onlyItem(
        collectRecordKeys(ticketStatusCategories).filter(
          (ticketStatusCategory) =>
            ticketStatusCategories[ticketStatusCategory].defaultTagId === ticketStatusTagId,
        ),
      ),
  );
}
